import { Building2, Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useInvitationPreview } from "@/hooks/use-invitation-preview";
import { authClient } from "@/lib/auth-client";
import { isConsumerSession } from "@/lib/auth-roles";
import { AcceptInvitationModal } from "./components/accept-invitation-modal";
import { InvitationAuth } from "./components/invitation-auth";

export default function AcceptInvitation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: session, isPending: isSessionPending } = authClient.useSession();
  const { data: invitation, isLoading: isInvitationLoading, error } = useInvitationPreview(id);

  if (!id) {
    return <ErrorCard message="Invitación inválida" />;
  }

  if (isSessionPending || isInvitationLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error || !invitation) {
    return <ErrorCard message="No encontramos esta invitación" />;
  }

  if (invitation.status !== "pending") {
    const statusMessages: Record<string, string> = {
      accepted: "Esta invitación ya fue aceptada",
      rejected: "Esta invitación fue rechazada",
      cancelled: "Esta invitación fue cancelada",
    };
    return <ErrorCard message={statusMessages[invitation.status] || "Invitación no disponible"} />;
  }

  if (new Date(invitation.expiresAt) < new Date()) {
    return <ErrorCard message="Esta invitación expiró" />;
  }

  const sessionEmail = session?.user?.email?.toLowerCase();
  const invitationEmail = invitation.email.toLowerCase();
  const emailMatches = sessionEmail === invitationEmail;
  const sessionIsConsumer = isConsumerSession(session);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md space-y-6">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              {invitation.organization.logo ? (
                <img
                  alt={invitation.organization.name ?? ""}
                  className="h-16 w-16 rounded-full"
                  height={64}
                  src={invitation.organization.logo}
                  width={64}
                />
              ) : (
                <Building2 className="h-8 w-8" />
              )}
            </div>
            <CardTitle>Te invitaron a {invitation.organization.name}</CardTitle>
            <CardDescription>
              {invitation.inviter.name ?? invitation.inviter.email} te invitó a unirte como{" "}
              <strong>{invitation.role}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {session ? (
              sessionIsConsumer ? (
                <ConsumerAccountMismatch />
              ) : emailMatches ? (
                <p className="text-center text-muted-foreground text-sm">
                  Confirmá la invitación en el diálogo.
                </p>
              ) : (
                <EmailMismatch
                  invitationEmail={invitation.email}
                  sessionEmail={session.user.email}
                />
              )
            ) : (
              <InvitationAuth email={invitation.email} invitationId={id} />
            )}
          </CardContent>
        </Card>

        {!session && (
          <p className="text-center text-muted-foreground text-xs">
            Al aceptar, te unirás a la organización. La suscripción la gestiona el propietario.
          </p>
        )}
      </div>

      {session && emailMatches && !sessionIsConsumer && (
        <AcceptInvitationModal invitation={invitation} onClose={() => navigate("/")} open />
      )}
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Invitación no disponible</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button className="w-full" onClick={() => navigate("/")}>
            Volver al inicio
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function ConsumerAccountMismatch() {
  const navigate = useNavigate();
  const handleSignOut = async () => {
    await authClient.signOut();
    toast.success("Sesión cerrada");
    navigate(0);
  };

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        Esta invitación es para una cuenta de restaurante. Cerrá sesión y continuá con una cuenta de
        restaurante para aceptarla.
      </p>
      <Button className="w-full" onClick={handleSignOut} variant="outline">
        Cerrar sesión y continuar
      </Button>
    </div>
  );
}

function EmailMismatch({
  invitationEmail,
  sessionEmail,
}: {
  invitationEmail: string;
  sessionEmail: string;
}) {
  const navigate = useNavigate();
  const handleSignOut = async () => {
    await authClient.signOut();
    toast.success("Sesión cerrada");
    navigate(0);
  };

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground text-sm">
        Esta invitación es para <strong>{invitationEmail}</strong>, pero estás con sesión de{" "}
        <strong>{sessionEmail}</strong>.
      </p>
      <Button className="w-full" onClick={handleSignOut} variant="outline">
        Cerrar sesión y continuar
      </Button>
    </div>
  );
}
